import { Component, createRef } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"

import displayFlash from "partners/scripts/display_flash"
import fetchAuthenticityToken from "app/scripts/refresh_form_authenticity_token"
import Modal from "shared/components/modal"
import WarningBox from "shared/components/boxes/warning_box"

import styles from "./import_service.module.scss"

// Destructive last, and never preselected.
const ACTIONS = ["create", "staging_clone", "reimport"]
const PRESELECTABLE_ACTIONS = ["create", "staging_clone"]

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? "" : "s"}`

const ACTION_COPY = {
  create: {
    label: () => "Create a new service",
    detail: ({ plan, organizationName }) => `Creates ${plan.module_name} in ${organizationName}.`,
    submit: "Create service",
  },
  staging_clone: {
    label: () => "Create a staging clone",
    detail: ({ plan }) => `${plan.module_name} stays as it is. The clone gets a _staging_ slug.`,
    submit: "Create staging clone",
  },
  reimport: {
    label: plan => `Replace ${plan.module_name} in place`,
    detail: ({ option }) => `Destroys its Applets and ${pluralize(option.connection_count, "connection")}.`,
    submit: "Replace service",
  },
}

export default class ImportService extends Component {
  state = {
    file: null,
    plan: null,
    action: null,
    preserveOauthClient: false,
    preserveApiUrlPrefix: false,
    submitting: false,
  }

  fileInputRef = createRef()

  pickFile = e => {
    e?.preventDefault()
    this.fileInputRef.current.click()
  }

  handleFileSelected = async e => {
    const file = e.target.files[0]
    // Reset so the same file can be picked again
    e.target.value = null
    if (!file) return

    this.setState({ file, plan: null, action: null })

    const plan = await this.post(this.props.importPlanUri, this.formData(file), "Couldn't read the service definition.")
    if (!plan) {
      this.setState({ file: null })
      return
    }

    const available = action => plan.options.some(option => option.action === action && option.available)
    this.setState({
      plan,
      action: PRESELECTABLE_ACTIONS.find(available) ?? null,
      preserveOauthClient: plan.preserve_oauth_client_default,
      preserveApiUrlPrefix: false,
    })
  }

  handleConfirm = async () => {
    const { file, action, preserveOauthClient, preserveApiUrlPrefix } = this.state
    const fields = { import_action: action }
    if (action === "reimport") {
      fields.preserve_oauth_client = String(preserveOauthClient)
      fields.preserve_api_url_prefix = String(preserveApiUrlPrefix)
    }

    this.setState({ submitting: true })
    displayFlash("Importing service…")

    const result = await this.post(this.props.importUri, this.formData(file, fields), "Import failed. Please try again.")
    if (!result) {
      this.setState({ submitting: false })
      return
    }

    window.location.assign(result.redirect_url)
  }

  handleCancel = () => {
    if (this.state.submitting) return
    this.setState({ file: null, plan: null, action: null })
  }

  formData(file, fields = {}) {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("organization_id", this.props.organizationId)
    Object.entries(fields).forEach(([name, value]) => formData.append(name, value))
    return formData
  }

  async post(uri, body, fallbackError) {
    try {
      const response = await fetch(uri, {
        method: "POST",
        headers: { "X-CSRF-Token": await fetchAuthenticityToken() },
        body,
      })
      const data = await response.json()
      if (response.ok) return data

      displayFlash(data.error || fallbackError, "alert-warning")
    } catch {
      displayFlash(fallbackError, "alert-warning")
    }
    return null
  }

  renderOption(actionName) {
    const { plan, action, submitting } = this.state
    const option = plan.options.find(candidate => candidate.action === actionName)
    const copy = ACTION_COPY[actionName]

    return (
      <div key={actionName}>
        <label className={classnames(styles.option, { [styles.unavailable]: !option.available })}>
          <input
            type="radio"
            name="import_action"
            value={actionName}
            checked={action === actionName}
            disabled={!option.available || submitting}
            onChange={() => this.setState({ action: actionName })}
          />
          {" "}
          {copy.label(plan)}
          <small className={styles.help}>
            {option.available ? copy.detail({ plan, organizationName: this.props.organizationName, option }) : option.refusal}
          </small>
        </label>
        {actionName === "reimport" && action === "reimport" && this.renderReimportFlags()}
      </div>
    )
  }

  renderReimportFlags() {
    const { plan, preserveOauthClient, preserveApiUrlPrefix, submitting } = this.state

    return (
      <div className={styles.flags}>
        <WarningBox message="Existing applets, statements, and live channels for this service will be permanently destroyed." />
        <label className={classnames("checkbox", styles.flag)}>
          <input
            type="checkbox"
            checked={preserveOauthClient}
            onChange={e => this.setState({ preserveOauthClient: e.target.checked })}
            disabled={submitting}
          />
          <span />
          <span>
            Keep the current OAuth client
            <small className={styles.help}>
              Uses {plan.module_name}&apos;s OAuth client ID and secret instead of the file&apos;s.
              {plan.preserve_oauth_client_default &&
                " On by default because the file's client_secret is a placeholder."}
            </small>
          </span>
        </label>
        <label className={classnames("checkbox", styles.flag)}>
          <input
            type="checkbox"
            checked={preserveApiUrlPrefix}
            onChange={e => this.setState({ preserveApiUrlPrefix: e.target.checked })}
            disabled={submitting}
          />
          <span />
          <span>
            Keep the current API URL prefix
            <small className={styles.help}>
              Current <code>{plan.current_api_url_prefix || "(none)"}</code>, file{" "}
              <code>{plan.definition_api_url_prefix || "(none)"}</code>
            </small>
          </span>
        </label>
      </div>
    )
  }

  renderBody() {
    const { file, plan, action, submitting } = this.state
    if (!plan) return <p className={styles.intro}>Reading {file.name}…</p>

    return (
      <div className={styles.body}>
        <div className={styles["file-row"]}>
          <span className={styles["file-name"]}>
            <strong>File:</strong> {file.name}
          </span>
          <button
            type="button"
            className="button-secondary button-cancel-link"
            onClick={this.pickFile}
            disabled={submitting}
          >
            Choose a different file
          </button>
        </div>

        <p className={styles.intro}>
          The file defines <strong>{plan.module_name}</strong>. Import it into {this.props.organizationName} as:
        </p>

        <div className={styles.options}>{ACTIONS.map(actionName => this.renderOption(actionName))}</div>

        <div className="action-buttons-container">
          <button
            type="button"
            className="button-secondary"
            onClick={this.handleConfirm}
            disabled={!action || submitting}
          >
            {submitting ? "Importing…" : ACTION_COPY[action]?.submit ?? "Import"}
          </button>
          <button
            type="button"
            className="button-secondary button-cancel-link"
            onClick={this.handleCancel}
            disabled={submitting}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  render() {
    return (
      <>
        {this.props.linkText && (
          <a href="#" onClick={this.pickFile}>
            {this.props.linkText}
          </a>
        )}
        <input
          type="file"
          ref={this.fileInputRef}
          accept="application/json"
          style={{ display: "none" }}
          onChange={this.handleFileSelected}
        />
        <Modal
          show={this.state.file !== null}
          closeListener={this.handleCancel}
          headerText="Import Service"
          includeCloseX={false}
          leftJustifyHeader
        >
          {this.state.file && this.renderBody()}
        </Modal>
      </>
    )
  }
}

ImportService.propTypes = {
  organizationId: PropTypes.number.isRequired,
  organizationName: PropTypes.string.isRequired,
  importPlanUri: PropTypes.string.isRequired,
  importUri: PropTypes.string.isRequired,
  linkText: PropTypes.string,
}
